import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';

import { type ListingDetails, listingPhotoUrl } from './listingDetails';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/**
 * The listing summary beside every wizard step: photo, address, prices, and
 * the step's Continue action. Sticky on desktop so the action stays at hand
 * on long steps.
 */
export const SummaryCard = ({
  listing,
  offerCents,
  continueLabel,
  onContinue,
  analyticsName,
  continueDisabled = false,
  note,
}: {
  listing: ListingDetails;
  offerCents: number | null | undefined;
  continueLabel: string;
  onContinue: () => void;
  analyticsName: string;
  /** Blocks the step's action while its answers are incomplete. */
  continueDisabled?: boolean;
  /** Reassurance under the action, per design on the review step. */
  note?: string;
}) => {
  const photo = listingPhotoUrl(listing);
  return (
    <Col className="w-full gap-6 rounded-[16px] bg-od-bg-secondary p-4 lg:sticky lg:top-6 lg:w-[267px] lg:shrink-0">
      <Box
        className={cn(
          'h-[146px] w-full overflow-hidden rounded-[8px]',
          !photo && 'bg-od-bg-inactive-secondary',
        )}
      >
        {photo ? <Image src={photo} alt="" className="size-full object-cover" /> : null}
      </Box>
      <Col className="gap-4">
        <Text
          as="h2"
          className="font-medium text-[20px] tracking-[-0.28px] text-od-content-primary"
        >
          {listing.address.human_readable}
        </Text>
        <Col className="gap-1">
          <Row className="justify-between">
            <Text className="text-body-md text-od-content-secondary">List price</Text>
            <Text className="text-body-md text-od-content-primary">
              {listing.listing_price_cents != null
                ? usd.format(listing.listing_price_cents / 100)
                : '-'}
            </Text>
          </Row>
          <Row className="justify-between">
            <Text className="text-body-md text-od-content-secondary">Your offer</Text>
            <Text className="font-medium text-body-md text-od-content-primary">
              {offerCents != null && offerCents > 0 ? usd.format(offerCents / 100) : '-'}
            </Text>
          </Row>
        </Col>
        <Col className="gap-2">
          <Button
            type="button"
            variant="primary"
            onClick={onContinue}
            // aria-disabled, not disabled: the step's guard already refuses to
            // advance, and a truly disabled button swallows the click that
            // asks the step to show what is still missing.
            aria-disabled={continueDisabled}
            className={cn(
              'w-full rounded-[12px]',
              continueDisabled &&
                'aria-disabled:border-transparent aria-disabled:bg-od-bg-inverse-inactive-primary aria-disabled:!text-od-content-tertiary aria-disabled:hover:bg-od-bg-inverse-inactive-primary',
            )}
            aria-label={continueLabel}
            analyticsName={analyticsName}
          >
            {continueLabel}
          </Button>
          {note != null ? (
            <Text className="text-center text-body-sm text-od-content-secondary">{note}</Text>
          ) : null}
        </Col>
      </Col>
    </Col>
  );
};
